import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLoadMoreAtBottom } from 'components/pull-to-reload/pull-to-reload';
import { useLoyaltyUserTransactionsQuery } from 'generated/graphql-gateway';
import { useLocalizedTransactionTransformer } from 'pages/loyalty/transactions/use-localized-transaction-transformer';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

const ROWS_PER_PAGE = 2;
const INITIAL_VISIBLE_TRANSACTIONS = 1;

export const useLoyaltyHistory = () => {
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const transactionTransformer = useLocalizedTransactionTransformer();
  // Prevents a brief flicker when `transactionsLoading` transitions from false (initial) to true
  const transactionsLoadingStarted = useRef<boolean>(false);

  const [visibleTransactions, setVisibleTransactions] = useState(INITIAL_VISIBLE_TRANSACTIONS);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, loading, refetch: refetchTransactionHistory } = useLoyaltyUserTransactionsQuery({
    skip: !loyaltyId,
    variables: { loyaltyId },
  });

  const transactions = data?.loyaltyUserV2?.transactions;

  const tableData = useMemo(() => {
    if (!transactions?.length) {
      return [];
    }

    const filteredTransactions = transactions.filter(transaction => {
      const { pointsEarned, pointsUsed, pointsDeducted, bonusPointsEarned } = transaction;
      return Boolean(bonusPointsEarned || pointsEarned || pointsUsed || pointsDeducted);
    });

    return filteredTransactions.map(transactionTransformer);
  }, [transactions, transactionTransformer]);

  useEffect(() => {
    if (loading) {
      transactionsLoadingStarted.current = true;
    }
  }, [loading]);

  useEffect(() => {
    if (loyaltyId) {
      refetchTransactionHistory({ loyaltyId });
    }
  }, [loyaltyId, refetchTransactionHistory]);

  const loadMoreHistoryTransactions = useCallback(() => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);
    requestAnimationFrame(() => {
      setVisibleTransactions(prev => prev + ROWS_PER_PAGE);
      setLoadingMore(false);
    });
  }, [loadingMore]);

  const { onScroll } = useLoadMoreAtBottom(loadMoreHistoryTransactions);

  return {
    tableData,
    transactionsLoading: loading || !transactionsLoadingStarted.current,
    loyaltyId,

    visibleTransactions,
    loadingMore,
    onScroll,

    refetchTransactionHistory,
    loadMoreHistoryTransactions,
  };
};
